import React, { useState, useEffect, useRef } from 'react';
import './DraggableModal.css';

let globalZIndex = 1000;

function DraggableModal({ modalId, onClose, children, defaultPosition = null, className = '' }) {
    const [position, setPosition] = useState({ x: null, y: null });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [zIndex, setZIndex] = useState(globalZIndex);
    const modalRef = useRef(null);

    // Load position from localStorage on mount
    useEffect(() => {
        const savedPosition = localStorage.getItem(`modal-position-${modalId}`);
        if (savedPosition) {
            try {
                const { x, y } = JSON.parse(savedPosition);
                // Validate position is within current viewport
                // Use a small margin to ensure modal is meaningfully visible
                const margin = 50;
                if (x >= 0 && x < window.innerWidth - margin && 
                    y >= 0 && y < window.innerHeight - margin) {
                    setPosition({ x, y });
                } else {
                    // Saved position is off-screen, center instead
                    setPosition({ x: null, y: null });
                }
            } catch (e) {
                console.error('Failed to parse saved modal position:', e);
                // Fall back to centering
                setPosition({ x: null, y: null });
            }
        } else if (defaultPosition) {
            setPosition(defaultPosition);
        } else {
            // Center modal on screen - will be set after first render
            setPosition({ x: null, y: null });
        }
    }, [modalId, defaultPosition]);

    // Set initial centered position after first render
    useEffect(() => {
        if (position.x === null && position.y === null && modalRef.current) {
            // Use setTimeout to ensure the modal is rendered and has dimensions
            const timer = setTimeout(() => {
                if (modalRef.current) {
                    const rect = modalRef.current.getBoundingClientRect();
                    const x = Math.max(0, (window.innerWidth - rect.width) / 2);
                    const y = Math.max(0, (window.innerHeight - rect.height) / 2);
                    setPosition({ x, y });
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [position.x, position.y]);

    // Handle ESC key to close modal (only if this is the topmost modal)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && modalRef.current) {
                // Check if this modal has the highest z-index (is topmost)
                const allModals = document.querySelectorAll('.draggable-modal');
                let maxZIndex = 0;
                let topmostModal = null;
                
                allModals.forEach(modal => {
                    const zIndex = parseInt(window.getComputedStyle(modal).zIndex) || 0;
                    if (zIndex > maxZIndex) {
                        maxZIndex = zIndex;
                        topmostModal = modal;
                    }
                });
                
                // Only close if this modal is the topmost one
                if (topmostModal === modalRef.current) {
                    onClose();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Attach drag handler using event delegation
    useEffect(() => {
        if (!modalRef.current) return;

        const handleMouseDown = (e) => {
            // Only handle left mouse button (button 0), ignore right-clicks and middle-clicks
            if (e.button !== 0) return;
            
            // Check if the click originated from a header element
            const header = e.target.closest('.modal-header, .tmodal-header');
            if (!header) return;
            
            // Don't drag if clicking on close button
            if (e.target.classList.contains('close') || e.target.closest('.close')) {
                return;
            }
            
            setIsDragging(true);
            const rect = modalRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            
            // Bring to front when starting drag
            globalZIndex += 10;
            setZIndex(globalZIndex);
            
            e.preventDefault();
        };

        const modal = modalRef.current;
        modal.addEventListener('mousedown', handleMouseDown);

        return () => {
            modal.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    // Bring modal to front on click
    const handleModalClick = () => {
        globalZIndex += 10;
        setZIndex(globalZIndex);
    };

    // Handle drag
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            if (!modalRef.current) return;

            const rect = modalRef.current.getBoundingClientRect();
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            // Keep modal within viewport bounds
            const maxX = window.innerWidth - rect.width;
            const maxY = window.innerHeight - rect.height;
            
            const constrainedX = Math.max(0, Math.min(newX, maxX));
            const constrainedY = Math.max(0, Math.min(newY, maxY));

            setPosition({ x: constrainedX, y: constrainedY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            // Save position to localStorage
            if (modalRef.current) {
                const rect = modalRef.current.getBoundingClientRect();
                localStorage.setItem(`modal-position-${modalId}`, JSON.stringify({
                    x: rect.left,
                    y: rect.top
                }));
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, modalId]);

    const modalStyle = {
        position: 'fixed',
        left: position.x !== null ? `${position.x}px` : '50%',
        top: position.y !== null ? `${position.y}px` : '50%',
        transform: position.x !== null && position.y !== null ? 'none' : 'translate(-50%, -50%)',
        zIndex: zIndex,
        ...(isDragging && { cursor: 'grabbing' })
    };

    return (
        <div 
            className={`draggable-modal ${className}`}
            ref={modalRef}
            style={modalStyle}
            onClick={handleModalClick}
        >
            {children}
        </div>
    );
}

export default DraggableModal;
